import { useMemo, useState, useEffect } from "react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CashDialog from "@/components/dashboard/CashDialog";
import { PinConfirmDialog } from "@/components/common/PinPad";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  HandCoins, Smartphone, CheckCircle2, Clock, CreditCard, Banknote, Ticket,
  RotateCcw, XCircle, SplitSquareHorizontal, AlertTriangle, Wallet,
} from "lucide-react";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return ""; }
}

const CASH_LABEL = { CB: "CB (TPE)", ESPECES: "Espèces", TICKET: "Ticket resto", AUTRE: "Autre" };

function channelInfo(p) {
  if (p.method === "stripe") return { label: "Carte (Stripe)", Icon: Smartphone, color: "text-emerald-400" };
  const cm = p.cash_method || "AUTRE";
  const Icon = cm === "TICKET" ? Ticket : cm === "ESPECES" ? Banknote : CreditCard;
  return { label: CASH_LABEL[cm] || "Comptoir", Icon, color: "text-[#C69C4A]" };
}

const SETTLE_METHODS = [["CB", "CB physique"], ["ESPECES", "Espèces"], ["TICKET", "Ticket resto"], ["AUTRE", "Autre"]];

export default function PaymentsPanel({ slug, payments = [], overview, onRefresh, isAdmin, focusTable, focusRemaining, focusNonce }) {
  const [seg, setSeg] = useState("collect");
  const [channel, setChannel] = useState("all"); // all | stripe | cash (Payé section)
  const [refundTarget, setRefundTarget] = useState(null);
  const [detailPayment, setDetailPayment] = useState(null);
  const [detailTable, setDetailTable] = useState(null);
  const [cashPending, setCashPending] = useState(null);
  const [cashTable, setCashTable] = useState(null);

  // Action rapide depuis l'alerte « paiement échoué » : ouvre directement l'encaissement
  // espèces de la table concernée (flux existant, montant = reste réel).
  useEffect(() => {
    if (focusNonce && focusTable != null) {
      setSeg("partial");
      setCashTable({ table_number: String(focusTable), remaining: focusRemaining || 0 });
    }
    // eslint-disable-next-line
  }, [focusNonce]);

  // Fallback to client-side split if overview not yet loaded
  const data = useMemo(() => {
    if (overview) return overview;
    return {
      to_collect: payments.filter((p) => p.method === "cash" && p.payment_status === "pending"),
      partial_tables: [],
      paid: payments.filter((p) => p.payment_status === "paid"),
      failed: payments.filter((p) => ["failed", "expired"].includes(p.payment_status)),
      refunded: payments.filter((p) => (p.refunded_amount || 0) > 0),
      totals: {},
    };
  }, [overview, payments]);

  const settle = async (id, method) => {
    try { await api.settlePayment(slug, id, method); toast.success(`Encaissé · ${CASH_LABEL[method]}`); onRefresh(); }
    catch (e) { toast.error("Erreur lors de l'encaissement"); }
  };

  const SEGMENTS = [
    { key: "collect", label: "À encaisser", icon: HandCoins, count: data.to_collect.length, urgent: data.to_collect.length > 0 },
    { key: "partial", label: "Partiellement payé", icon: SplitSquareHorizontal, count: data.partial_tables.length },
    { key: "paid", label: "Payé", icon: CheckCircle2, count: data.paid.length },
    { key: "refunded", label: "Remboursés", icon: RotateCcw, count: data.refunded.length },
    { key: "failed", label: "Échecs", icon: XCircle, count: data.failed.length },
  ];

  const paidFiltered = data.paid.filter((p) => channel === "all" || p.method === (channel === "stripe" ? "stripe" : "cash"));
  const t = data.totals || {};

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MiniKpi testid="pay-kpi-collect" icon={HandCoins} label="À encaisser" value={eur(t.to_collect || 0)} sub={`${data.to_collect.length} demande(s)`} accent="#C69C4A" onClick={() => setSeg("collect")} />
        <MiniKpi testid="pay-kpi-partial" icon={SplitSquareHorizontal} label="Reste partiel" value={eur(t.partial_remaining || 0)} sub={`${data.partial_tables.length} table(s)`} accent="#E7C97A" onClick={() => setSeg("partial")} />
        <MiniKpi testid="pay-kpi-paid" icon={Wallet} label="Encaissé" value={eur((t.paid?.total) || 0)} sub={`Carte ${eur(t.paid?.stripe || 0)} · Comptoir ${eur(t.paid?.cash || 0)}`} accent="#34D399" onClick={() => setSeg("paid")} />
        <MiniKpi testid="pay-kpi-refunded" icon={RotateCcw} label="Remboursé" value={eur(t.refunded || 0)} sub={`${data.refunded.length} opération(s)`} accent="#F87171" onClick={() => setSeg("refunded")} />
      </div>

      {/* Segmented control */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid="payments-segments">
        {SEGMENTS.map((s) => {
          const Icon = s.icon;
          const active = seg === s.key;
          return (
            <button
              key={s.key}
              data-testid={`pay-seg-${s.key}`}
              onClick={() => setSeg(s.key)}
              className={`shrink-0 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                active ? "bg-[#1A3D32] text-white border-[#2C5446]" : "bg-[#111F1A] text-stone-400 border-[#1B3028] hover:text-stone-200"
              }`}
            >
              <Icon className={`w-4 h-4 ${s.urgent && !active ? "text-[#C69C4A]" : ""}`} /> {s.label}
              {s.count > 0 && <span className={`text-[10px] font-bold rounded-full px-1.5 py-0.5 ${active ? "bg-[#C69C4A] text-[#0C1713]" : "bg-[#1B3028] text-stone-300"}`}>{s.count}</span>}
            </button>
          );
        })}
      </div>

      {/* À ENCAISSER */}
      {seg === "collect" && (
        data.to_collect.length === 0 ? <Empty testid="no-pending-payments" text="Aucune demande d'encaissement en attente." />
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.to_collect.map((p) => (
              <div key={p.id} data-testid={`payment-pending-${p.table_number}`} onClick={() => setDetailPayment(p)} className="rounded-2xl bg-[#1C1210] border border-[#C69C4A]/40 p-4 cursor-pointer hover:border-[#C69C4A]/70 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-serif-display text-lg text-white">Table {p.table_number}</span>
                  <span className="text-xs text-[#C69C4A] flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtTime(p.created_at)}</span>
                </div>
                <p className="font-bold text-2xl text-white mt-2">{eur(p.amount)}</p>
                <p className="text-xs text-stone-400 mt-0.5">Encaissement au comptoir · <span className="underline">voir le détail</span></p>
                <p className="text-[11px] text-stone-400 mt-3 mb-1.5">Encaissé via :</p>
                <div className="grid grid-cols-2 gap-1.5" data-testid={`settle-methods-${p.id}`}>
                  {SETTLE_METHODS.map(([m, l]) => (
                    <button key={m} data-testid={`settle-${m}-${p.id}`} onClick={(e) => { e.stopPropagation(); if (m === "ESPECES") setCashPending(p); else settle(p.id, m); }}
                      className="rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold text-xs py-2 transition-colors">{l}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* PARTIELLEMENT PAYÉ */}
      {seg === "partial" && (
        data.partial_tables.length === 0 ? <Empty testid="no-partial-tables" text="Aucune table partiellement réglée." />
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.partial_tables.map((tbl) => (
              <div key={tbl.table_number} data-testid={`partial-table-${tbl.table_number}`} onClick={() => setDetailTable(tbl)} className="rounded-2xl bg-[#111F1A] border border-[#E7C97A]/40 p-4 cursor-pointer hover:border-[#E7C97A]/70 transition-colors">
                <div className="flex items-center justify-between">
                  <span className="font-serif-display text-lg text-white">Table {tbl.table_number}</span>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#E7C97A] bg-[#E7C97A]/10 px-2 py-0.5 rounded-full">Partiel</span>
                </div>
                <div className="mt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-stone-400"><span>Addition</span><span className="text-stone-200 font-semibold">{eur(tbl.bill_total)}</span></div>
                  <div className="flex justify-between text-stone-400"><span>Déjà réglé</span><span className="text-emerald-400 font-semibold">{eur(tbl.paid_amount)}</span></div>
                  {tbl.pending_amount > 0 && <div className="flex justify-between text-stone-400"><span>En attente comptoir</span><span className="text-[#C69C4A] font-semibold">{eur(tbl.pending_amount)}</span></div>}
                  <div className="flex justify-between border-t border-[#1B3028] pt-1.5 mt-1.5"><span className="text-stone-200 font-semibold">Reste à payer</span><span className="text-white font-bold text-lg">{eur(tbl.remaining)}</span></div>
                </div>
                <p className="text-[11px] text-stone-500 mt-2 underline">Voir le détail de la table</p>
                <button data-testid={`partial-cash-${tbl.table_number}`} onClick={(e) => { e.stopPropagation(); setCashTable(tbl); }}
                  className="mt-2 w-full rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-semibold text-xs py-2 transition-colors">Encaisser (espèces / TPE)</button>
              </div>
            ))}
          </div>
        )
      )}

      {/* PAYÉ (with channel separation) */}
      {seg === "paid" && (
        <>
          <div className="flex gap-2" data-testid="paid-channel-filter">
            {[["all", "Tous"], ["stripe", "Carte (Stripe)"], ["cash", "Espèces / Comptoir"]].map(([k, l]) => (
              <button key={k} data-testid={`paid-channel-${k}`} onClick={() => setChannel(k)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold border transition-colors ${channel === k ? "bg-[#1A3D32] text-white border-[#2C5446]" : "bg-[#111F1A] text-stone-400 border-[#1B3028] hover:text-stone-200"}`}>{l}</button>
            ))}
          </div>
          {paidFiltered.length === 0 ? <Empty testid="no-payment-history" text="Aucun paiement dans ce canal." />
          : <PaymentTable rows={paidFiltered} isAdmin={isAdmin} onRefund={setRefundTarget} onRowClick={setDetailPayment} />}
        </>
      )}

      {/* REMBOURSÉS */}
      {seg === "refunded" && (
        data.refunded.length === 0 ? <Empty testid="no-refunds" text="Aucun remboursement enregistré." />
        : <PaymentTable rows={data.refunded} isAdmin={isAdmin} onRefund={setRefundTarget} onRowClick={setDetailPayment} showRefund />
      )}

      {/* ÉCHECS */}
      {seg === "failed" && (
        data.failed.length === 0 ? <Empty testid="no-failed" text="Aucun paiement échoué ou expiré." />
        : (
          <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] overflow-hidden">
            <table className="w-full text-sm" data-testid="failed-table">
              <thead><tr className="text-left text-stone-400 border-b border-[#1B3028]">
                <th className="px-4 py-3 font-medium">Table</th><th className="px-4 py-3 font-medium">Montant</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Date</th><th className="px-4 py-3 font-medium">Statut</th>
              </tr></thead>
              <tbody>{data.failed.map((p) => (
                <tr key={p.id} data-testid={`failed-row-${p.id}`} onClick={() => setDetailPayment(p)} className="border-b border-[#1B3028] last:border-0 text-stone-200 cursor-pointer hover:bg-[#0F1B16]">
                  <td className="px-4 py-3 font-semibold">Table {p.table_number}</td>
                  <td className="px-4 py-3">{eur(p.amount)}</td>
                  <td className="px-4 py-3 text-stone-400 hidden sm:table-cell">{fmtTime(p.updated_at || p.created_at)}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400"><XCircle className="w-3.5 h-3.5" /> {p.payment_status === "expired" ? "Expiré" : "Échec"}</span></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )
      )}

      <RefundDialog slug={slug} payment={refundTarget} onClose={() => setRefundTarget(null)} onDone={() => { setRefundTarget(null); onRefresh(); }} />
      <PaymentDetailDialog payment={detailPayment} isAdmin={isAdmin} onClose={() => setDetailPayment(null)} onRefund={(p) => { setDetailPayment(null); setRefundTarget(p); }} />
      <TableDetailDialog slug={slug} table={detailTable} data={data} onClose={() => setDetailTable(null)} onOpenPayment={(p) => { setDetailTable(null); setDetailPayment(p); }} />
      <CashDialog open={!!cashPending} onClose={() => setCashPending(null)} title={`Espèces · Table ${cashPending?.table_number}`} dueAmount={cashPending?.amount || 0} allowPartial={false}
        onConfirm={async (pl) => { await api.settlePayment(slug, cashPending.id, pl.method, { amount_received: pl.amount_received, tip: pl.tip }); toast.success(pl.change > 0 ? `Encaissé · monnaie à rendre ${eur(pl.change)}` : "Encaissé"); onRefresh(); }} />
      <CashDialog open={!!cashTable} onClose={() => setCashTable(null)} title={`Encaisser · Table ${cashTable?.table_number}`} dueAmount={cashTable?.remaining || 0} allowPartial
        onConfirm={async (pl) => { const r = await api.cashPayment(slug, cashTable.table_number, { amount: pl.amount, tip: pl.tip, amount_received: pl.amount_received, method: pl.method }); const recap = r.fully_paid ? `✅ Table ${cashTable.table_number} réglée` : `Table ${cashTable.table_number} · reste ${eur(r.remaining ?? 0)}`; toast.success(r.change_given ? `${recap} · monnaie à rendre ${eur(r.change_given)}` : recap, { duration: r.fully_paid ? 4000 : 6000 }); onRefresh(); }} />
    </div>
  );
}

function PaymentTable({ rows, isAdmin, onRefund, showRefund, onRowClick }) {
  return (
    <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] overflow-hidden">
      <table className="w-full text-sm" data-testid="payment-history-table">
        <thead>
          <tr className="text-left text-stone-400 border-b border-[#1B3028]">
            <th className="px-4 py-3 font-medium">Table</th>
            <th className="px-4 py-3 font-medium">Montant</th>
            <th className="px-4 py-3 font-medium">Canal</th>
            <th className="px-4 py-3 font-medium hidden sm:table-cell">Date</th>
            <th className="px-4 py-3 font-medium">Statut</th>
            {isAdmin && <th className="px-4 py-3 font-medium text-right">Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const ch = channelInfo(p);
            const refunded = (p.refunded_amount || 0) > 0;
            const fullyRefunded = p.refund_status === "full";
            const refundable = round2((p.amount || 0) - (p.refunded_amount || 0)) > 0.01;
            return (
              <tr key={p.id} data-testid={`payment-row-${p.id}`} onClick={() => onRowClick && onRowClick(p)} className="border-b border-[#1B3028] last:border-0 text-stone-200 cursor-pointer hover:bg-[#0F1B16]">
                <td className="px-4 py-3 font-semibold">Table {p.table_number}</td>
                <td className="px-4 py-3 font-bold text-white">
                  {eur(p.amount)}
                  {refunded && <span className="block text-[11px] font-normal text-red-400">− {eur(p.refunded_amount)} remb.</span>}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs ${ch.color}`}><ch.Icon className="w-3.5 h-3.5" /> {ch.label}</span>
                </td>
                <td className="px-4 py-3 text-stone-400 hidden sm:table-cell">{fmtTime(p.paid_at || p.created_at)}</td>
                <td className="px-4 py-3">
                  {fullyRefunded ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400"><RotateCcw className="w-3.5 h-3.5" /> Remboursé</span>
                  ) : refunded ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#E7C97A]"><RotateCcw className="w-3.5 h-3.5" /> Partiel remb.</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Réglé</span>
                  )}
                </td>
                {isAdmin && (
                  <td className="px-4 py-3 text-right">
                    {refundable ? (
                      <button data-testid={`refund-btn-${p.id}`} onClick={(e) => { e.stopPropagation(); onRefund(p); }} className="inline-flex items-center gap-1 text-xs font-semibold text-red-300 hover:text-red-200 rounded-full border border-red-500/30 hover:border-red-500/60 px-3 py-1.5 transition-colors">
                        <RotateCcw className="w-3.5 h-3.5" /> Rembourser
                      </button>
                    ) : <span className="text-[11px] text-stone-600">—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RefundDialog({ slug, payment, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [full, setFull] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  if (!payment) return null;
  const refundable = round2((payment.amount || 0) - (payment.refunded_amount || 0));
  const isCard = payment.method === "stripe";

  const runRefund = async (authorizedBy, pin) => {
    setPinOpen(false);
    const amt = full ? undefined : round2(parseFloat(amount) || 0);
    setSaving(true);
    try {
      const finalReason = [reason.trim(), authorizedBy ? `Autorisé par ${authorizedBy}` : ""].filter(Boolean).join(" · ") || null;
      await api.refundPayment(slug, payment.id, { amount: amt, reason: finalReason, pin });
      toast.success(isCard ? "Remboursement Stripe effectué" : "Remboursement enregistré");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const submit = () => {
    const amt = full ? undefined : round2(parseFloat(amount) || 0);
    if (!full && (!amt || amt <= 0)) { toast.error("Montant invalide"); return; }
    if (!full && amt > refundable + 0.01) { toast.error(`Maximum remboursable : ${eur(refundable)}`); return; }
    setPinOpen(true); // step-up PIN before a refund
  };

  return (
    <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="refund-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2"><RotateCcw className="w-5 h-5 text-red-400" /> Rembourser · Table {payment.table_number}</DialogTitle>
          <DialogDescription className="text-stone-400">
            {isCard ? "Remboursement via Stripe sur la carte du client." : "Remboursement au comptoir (enregistrement manuel)."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-xl bg-[#111F1A] border border-[#1B3028] p-3 text-sm flex justify-between">
            <span className="text-stone-400">Remboursable</span><span className="font-bold text-white">{eur(refundable)}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="refund-mode-full" onClick={() => setFull(true)} className={`rounded-xl py-2.5 text-sm font-semibold border transition-colors ${full ? "bg-[#1A3D32] text-white border-[#2C5446]" : "bg-[#111F1A] text-stone-400 border-[#1B3028]"}`}>Total</button>
            <button data-testid="refund-mode-partial" onClick={() => setFull(false)} className={`rounded-xl py-2.5 text-sm font-semibold border transition-colors ${!full ? "bg-[#1A3D32] text-white border-[#2C5446]" : "bg-[#111F1A] text-stone-400 border-[#1B3028]"}`}>Partiel</button>
          </div>
          {!full && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Montant à rembourser (€)</label>
              <Input data-testid="refund-amount" type="number" min="0" step="0.5" max={refundable} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Max ${eur(refundable)}`} className="mt-1.5 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-stone-400">Motif (facultatif)</label>
            <Input data-testid="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Ex : plat non servi" className="mt-1.5 bg-[#111F1A] border-[#1B3028] text-stone-100 rounded-xl" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Annuler</Button>
          <Button data-testid="refund-confirm" onClick={submit} disabled={saving} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold">
            {saving ? "..." : `Rembourser ${full ? eur(refundable) : (amount ? eur(round2(parseFloat(amount) || 0)) : "")}`}
          </Button>
        </DialogFooter>
      </DialogContent>
      <PinConfirmDialog open={pinOpen} onClose={() => setPinOpen(false)} slug={slug}
        title="Confirmer le remboursement"
        description="Action sensible : saisissez le PIN Manager/Propriétaire pour autoriser le remboursement."
        onConfirmed={runRefund} />
    </Dialog>
  );
}

function MiniKpi({ icon: Icon, label, value, sub, accent, testid, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} className="text-left w-full rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4 hover:border-[#C69C4A]/40 active:scale-[0.99] transition-all">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-lg grid place-items-center" style={{ backgroundColor: `${accent}1A` }}><Icon className="w-4 h-4" style={{ color: accent }} /></div>
        <span className="text-[11px] uppercase tracking-wider text-stone-500">{label}</span>
      </div>
      <p className="font-bold text-xl text-white">{value}</p>
      <p className="text-[11px] text-stone-500 mt-0.5">{sub}</p>
    </button>
  );
}

function Empty({ testid, text }) {
  return <p data-testid={testid} className="text-sm text-stone-500 bg-[#111F1A] border border-[#1B3028] rounded-2xl p-4">{text}</p>;
}

function DetailRow({ label, value, strong, accent }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-[#1B3028] last:border-0">
      <span className="text-stone-400 text-sm">{label}</span>
      <span className={`text-sm text-right ${strong ? "font-bold text-white" : "text-stone-200"}`} style={accent ? { color: accent } : {}}>{value}</span>
    </div>
  );
}

function statusLabel(p) {
  if (p.refund_status === "full") return "Remboursé";
  if ((p.refunded_amount || 0) > 0) return "Partiellement remboursé";
  if (p.payment_status === "paid") return "Réglé";
  if (p.payment_status === "pending") return "En attente d'encaissement";
  if (p.payment_status === "failed") return "Échec";
  if (p.payment_status === "expired") return "Expiré";
  return p.payment_status || "—";
}

function PaymentDetailDialog({ payment, isAdmin, onClose, onRefund }) {
  if (!payment) return null;
  const p = payment;
  const ch = channelInfo(p);
  const refundable = round2((p.amount || 0) - (p.refunded_amount || 0)) > 0.01 && ["paid", "refunded"].includes(p.payment_status);
  const guests = Array.isArray(p.covered_guests) ? p.covered_guests.filter(Boolean) : [];
  const splitLabel = { whole: "Toute la table", guest: "Par convive", equal: "Partage égal", amount: "Montant libre" }[p.split_mode] || p.split_mode;
  return (
    <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="payment-detail-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2">
            <ch.Icon className={`w-5 h-5 ${ch.color}`} /> Table {p.table_number}
          </DialogTitle>
          <DialogDescription className="text-stone-400">Détail du paiement · {statusLabel(p)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-0.5">
          <DetailRow label="Montant réglé" value={eur(p.amount)} strong />
          {p.bill_amount != null && p.bill_amount !== p.amount && <DetailRow label="Dont addition" value={eur(p.bill_amount)} />}
          {p.tip > 0 && <DetailRow label="Dont pourboire" value={eur(p.tip)} accent="#C69C4A" />}
          <DetailRow label="Canal" value={ch.label} />
          <DetailRow label="Mode de partage" value={splitLabel} />
          {guests.length > 0 && <DetailRow label="Convives couverts" value={guests.join(", ")} />}
          {(p.refunded_amount || 0) > 0 && <DetailRow label="Remboursé" value={`− ${eur(p.refunded_amount)}`} accent="#F87171" />}
          {p.refund_reason && <DetailRow label="Motif remb." value={p.refund_reason} />}
          {p.refunded_by && <DetailRow label="Remboursé par" value={p.refunded_by} />}
          {p.settled_by && <DetailRow label="Encaissé par" value={p.settled_by} />}
          {p.customer_email && <DetailRow label="Reçu envoyé à" value={p.customer_email} />}
          <DetailRow label="Créé le" value={fmtTime(p.created_at)} />
          {p.paid_at && <DetailRow label="Réglé le" value={fmtTime(p.paid_at)} />}
          {p.refunded_at && <DetailRow label="Remboursé le" value={fmtTime(p.refunded_at)} />}
          {p.routed_to_connect && <DetailRow label="Versé au compte" value="Compte connecté (Connect)" />}
          {p.stripe_payment_intent_id && <DetailRow label="Réf. Stripe" value={<span className="font-mono text-[11px]">{p.stripe_payment_intent_id}</span>} />}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Fermer</Button>
          {isAdmin && refundable && (
            <Button data-testid="detail-refund-btn" onClick={() => onRefund(p)} className="rounded-full bg-red-500 hover:bg-red-600 text-white font-semibold">
              <RotateCcw className="w-4 h-4 mr-1.5" /> Rembourser
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TableDetailDialog({ slug, table, data, onClose, onOpenPayment }) {
  if (!table) return null;
  const tn = table.table_number;
  // Payments already known for this table (paid + to_collect)
  const related = [...(data.to_collect || []), ...(data.paid || [])].filter((p) => String(p.table_number) === String(tn));
  return (
    <Dialog open={!!table} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="table-detail-dialog" className="max-w-md rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Table {tn} · en cours</DialogTitle>
          <DialogDescription className="text-stone-400">Répartition de l'addition</DialogDescription>
        </DialogHeader>
        <div className="space-y-0.5">
          <DetailRow label="Addition totale" value={eur(table.bill_total)} strong />
          <DetailRow label="Déjà réglé" value={eur(table.paid_amount)} accent="#34D399" />
          {table.pending_amount > 0 && <DetailRow label="En attente comptoir" value={eur(table.pending_amount)} accent="#C69C4A" />}
          <DetailRow label="Reste à payer" value={eur(table.remaining)} strong accent="#F87171" />
        </div>
        {related.length > 0 && (
          <div className="mt-2">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-500 mb-2">Paiements de cette table</p>
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {related.map((p) => {
                const ch = channelInfo(p);
                return (
                  <button key={p.id} data-testid={`table-payment-${p.id}`} onClick={() => onOpenPayment(p)} className="w-full flex items-center justify-between rounded-xl bg-[#111F1A] border border-[#1B3028] px-3 py-2 hover:border-[#C69C4A]/40 transition-colors text-left">
                    <span className={`inline-flex items-center gap-1.5 text-xs ${ch.color}`}><ch.Icon className="w-3.5 h-3.5" /> {ch.label}</span>
                    <span className="text-sm font-semibold text-white">{eur(p.amount)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full bg-transparent border-[#1B3028] text-stone-300">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
